import PageLayout from "@/Themes/Default/Layouts/PageLayout";
import {withAuthenticationRequired} from "@auth0/auth0-react";

import React, {useMemo, useState} from "react";
import styled from "styled-components";
import {Button} from "primereact/button";
import {useDebounce} from "react-use";
import BaseTextInput from "@/Components/Forms/Form/BaseTextInput";
import UserDatable from "@/Components/Datatables/UserDatable";
import {snakeToNormalCase, url} from "@/Helpers/general";
import {useNavigate} from "react-router-dom";
import BaseDropdown from "@/Components/Forms/Form/BaseDropdown";
import {Role} from "@/Stub/V1";
import useGetUsersFiltersQuery, {UserFilterType} from "@/Hooks/useGetUsersFiltersQuery";
import {useUpdateSearchParams} from "@/Hooks/useUpdateSearchParams";
import {useGetClients} from "@/Service/Api/V1/ApiHooks/Clients/useGetClients";
import AuditsDatatable from "@/Components/Datatables/AuditsDatatable";
import {useGetUsers} from "@/Service/Api/V1/ApiHooks/Users/useGetUsers";
import useGetAuditsFiltersQuery, {AuditFilterType} from "@/Hooks/useGetAuditsFiltersQuery";
import {can} from "@/Util/permissionChecks";

const StyledWrap = styled.main`
    .content-container {
        margin-top: 2rem;
        padding: 0 1rem;
    }

    .page-actions {
        margin-top: 2rem;
        padding: 1rem;
        background: #fff;
        border-radius: 8px;
    }
`;


const AuditsPage: React.FC = () => {
    const updateUsersFilterHandler = useUpdateSearchParams();
    const {
        pageQuery,
        searchQuery,
        pageSizeQuery,
        clientIdQuery,
        userIdQuery
    } = useGetAuditsFiltersQuery();

    const [inputValue, setInputValue] = useState(searchQuery || '');


    useDebounce(
        () => {
            updateUsersFilterHandler(UserFilterType.Search, inputValue);
        },
        1000,
        [inputValue]
    );

    const {data: clientsData} = useGetClients({
        requestParams: {
            page: 1,
            per_page: 1000
        }
    });
    const {data: usersData} = useGetUsers({
        requestParams: {
            page: 1,
            per_page: 1000
        }
    });

    const requestParams = useMemo(() => {
        return {
            page: pageQuery,
            per_page: pageSizeQuery,
            client_id: clientIdQuery === 'all' ? undefined : clientIdQuery,
            user_id: userIdQuery === 'all' ? undefined : userIdQuery,
            search: searchQuery || undefined
        };
    }, [searchQuery, pageQuery, pageSizeQuery, clientIdQuery, userIdQuery]);

    const organizationOptions = useMemo(() => {
        const clients = clientsData?.data || [];
        return [
            {key: 'all', label: 'All', value: 'all'},
            ...clients.map(client => ({
                key: client.id,
                label: client.name,
                value: client.id.toString()
            }))
        ];
    }, [clientsData]);

    const userOptions = useMemo(() => {
        const users = usersData?.data || [];
        return [
            {key: 'all', label: 'All', value: 'all'},
            ...users.map(user => ({
                key: user.id,
                label: user.name,
                value: user.id.toString()
            }))
        ];
    }, [usersData]);

    return (
        <PageLayout>
            <StyledWrap>
                <div className="flex justify-content-between content-header">
                    <div>
                        <h2 className="content-header__title">Audits</h2>
                    </div>
                </div>
                <div className="flex align-items-end gap-3">
                    <BaseTextInput
                        value={inputValue || ''}
                        placeholder="Search"
                        onChange={(e) => {
                            setInputValue(e.target.value);
                        }}
                    />
                    {
                        can('read:any_audit') ?
                            <>
                                <BaseDropdown
                                    name="Organization"
                                    value={clientIdQuery || 'all'}
                                    label="Organization"
                                    onChange={(e) => {
                                        updateUsersFilterHandler(AuditFilterType.ClientId, e.value);
                                    }}
                                    options={organizationOptions}
                                />
                                <BaseDropdown
                                    name="User"
                                    value={userIdQuery || 'all'}
                                    label="User"
                                    onChange={(e) => {
                                        updateUsersFilterHandler(AuditFilterType.UserId, e.value);
                                    }}
                                    options={userOptions}
                                />
                            </>
                            : undefined
                    }
                </div>
            </StyledWrap>

            <div className="content-container">
                <AuditsDatatable requestParams={requestParams}/>
            </div>
        </PageLayout>
    );
};

export default withAuthenticationRequired(AuditsPage);
